/**
 * Joining a group by typing the code.
 *
 * There was already a screen for this, but only ever reached by *following* an
 * invite link — so somebody who had been read a code over the phone, or sent
 * one in a message that lost its link, had nowhere to put it. The empty home
 * screen even said "or an access code" and then offered no box to type it into,
 * which is the worst version: the product names a way in and does not provide
 * one.
 *
 * The code is checked before anything is joined, so the group's name can be
 * shown and confirmed. An invite code is a bearer token — whoever holds it gets
 * in — and joining a stranger's group by mistyping one character is a thing
 * worth one extra look.
 */

import { useState } from "react";
import { useT } from "../../lib/i18n.js";
import { useNavigate } from "@tanstack/react-router";
import { Button } from "../../components/ui/Button.js";
import { Dialog } from "../../components/ui/Dialog.js";
import { Field } from "../../components/ui/Field.js";
import { Input } from "../../components/ui/Input.js";
import { errorMessage, trpcClient } from "../../lib/trpc.js";
import { joinGroupWithCode } from "./joinGroup.js";
// Its own styles live beside the switcher's, which is the other place it opens
// from. Imported here so the dialog carries them wherever it is used.
import "./GroupSwitcher.css";

export interface JoinGroupDialogProps {
  onClose: () => void;
  onJoined?: (groupId: string) => void;
}

/** What `auth.inspectInvite` tells us about a code, before it is spent. */
interface Preview {
  name: string;
  emoji: string | null;
}

export function JoinGroupDialog({ onClose, onJoined }: JoinGroupDialogProps) {
  const t = useT();
  const navigate = useNavigate();

  const [code, setCode] = useState("");
  const [preview, setPreview] = useState<Preview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
   * Codes are written down and read out, so they arrive with stray spaces and
   * in whatever case somebody's keyboard chose. The server compares them
   * exactly, which would turn "BARCELONA-rvbd" into "that code is not valid" —
   * a true statement about a code nobody typed.
   */
  const cleaned = code.trim().toUpperCase().replace(/\s+/g, "");

  async function check() {
    setError(null);
    setBusy(true);
    try {
      const invite = await trpcClient.auth.inspectInvite.query({
        code: cleaned,
      });
      if (!invite.valid) {
        setError("That code is not valid. Check it and try again.");
        return;
      }
      setPreview({ name: invite.group.name, emoji: invite.group.emoji });
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  async function join() {
    setError(null);
    setBusy(true);
    try {
      const { groupId, keyAdopted } = await joinGroupWithCode(cleaned);
      onJoined?.(groupId);
      if (!keyAdopted) {
        // Joined, but without the key. Said plainly rather than left to be
        // discovered as a gallery of photographs that will not open.
        setError(
          "You are in the group, but this browser could not get its key. " +
            "Ask someone already in it to open the group once.",
        );
      }
      await navigate({ to: "/g/$groupId", params: { groupId }, search: {} });
      onClose();
    } catch (cause) {
      setError(errorMessage(cause));
      // Back to the code, because a spent or revoked invite cannot be retried
      // by pressing the same button again.
      setPreview(null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      title={preview ? "Join this group?" : "Join a group"}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            {t("Cancel")}
          </Button>
          {preview ? (
            <Button
              variant="primary"
              loading={busy}
              onClick={() => void join()}
            >
              {t("Join")}
            </Button>
          ) : (
            <Button
              variant="primary"
              loading={busy}
              disabled={cleaned.length === 0}
              onClick={() => void check()}
            >
              {t("Continue")}
            </Button>
          )}
        </>
      }
    >
      {preview ? (
        <div className="fc-join-dialog__preview">
          <p className="fc-join-dialog__name">
            {preview.emoji && <span aria-hidden="true">{preview.emoji} </span>}
            {preview.name}
          </p>
          <p className="fc-join-dialog__note">
            {t(
              "Everything shared in this group is visible to everyone in it, and to nobody else.",
            )}
          </p>
          {error && (
            <p className="fc-join-dialog__error" role="alert">
              {error}
            </p>
          )}
        </div>
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (cleaned.length > 0) void check();
          }}
        >
          <Field
            label={t("Access code")}
            required
            error={error ?? undefined}
            hint={t("From whoever invited you. Case and spaces do not matter.")}
          >
            <Input
              value={code}
              autoFocus
              maxLength={64}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              placeholder="BARCELONA-RVBD-NX93-QRVB"
              disabled={busy}
              onChange={(event) => setCode(event.target.value)}
            />
          </Field>
        </form>
      )}
    </Dialog>
  );
}
